import { segmentKey, type GeoHint, type SegmentId } from "@p2p-hls/protocol";
import { PeerConnectionManager } from "./peer-manager.js";
import { PeerRegistry } from "./peer-registry.js";
import { SegmentScheduler, type HttpFetch } from "./scheduler.js";
import { SegmentStore } from "./segment-store.js";
import { SignalingClient } from "./signaling-client.js";
import { EngineMetrics } from "./metrics.js";
import { DEFAULT_CONFIG, type EngineConfig, type SegmentResult, type Telemetry } from "./types.js";

export type EngineOptions = Pick<EngineConfig, "trackerUrl" | "streamId" | "iceServers"> &
  Partial<EngineConfig>;

/** Strip `readonly` so the shared config object's knobs can be hot-reloaded. */
type Mutable<T> = { -readonly [K in keyof T]: T[K] };

/**
 * The tuning knobs that can be changed on a running engine (hot-reload from the
 * tracker's /config). Every one is read live from `this.config` by the engine or
 * a sub-component that holds the same config reference, so mutating it in place
 * takes effect on the next read — except the metrics timer, restarted explicitly.
 */
export type HotKnob =
  | "maxPeers"
  | "peerRequestConcurrency"
  | "prefetchAhead"
  | "maxConcurrentPrefetch"
  | "metricsSampleRate"
  | "metricsReportIntervalMs"
  // Latency/anti-rebuffer knobs — adapted per connection class on mobile so the
  // engine falls back to the CDN sooner on a weak link (see network.watchConnection).
  | "httpOnlyDeadlineMs"
  | "hedgeAtDeadlineFraction"
  | "peerRequestTimeoutMs"
  | "maxPeerRttMs";
const HOT_KNOBS: readonly HotKnob[] = [
  "maxPeers",
  "peerRequestConcurrency",
  "prefetchAhead",
  "maxConcurrentPrefetch",
  "metricsSampleRate",
  "metricsReportIntervalMs",
  "httpOnlyDeadlineMs",
  "hedgeAtDeadlineFraction",
  "peerRequestTimeoutMs",
  "maxPeerRttMs",
];

/**
 * Top-level P2P engine. Owns the signaling client, WebRTC mesh, segment store,
 * peer registry, and scheduler, and exposes two things the player integration
 * needs: `activate()` (call on `play`) and `getSegment()` (call from the loader).
 */
export class P2PEngine {
  readonly peerId: string;
  /** Rich metrics (source mix, latency/RTT quantiles, deadline-miss, series). */
  readonly metrics = new EngineMetrics();
  private readonly config: EngineConfig;
  private readonly store: SegmentStore;
  private readonly registry: PeerRegistry;
  private readonly signaling: SignalingClient;
  private readonly manager: PeerConnectionManager;
  private readonly scheduler: SegmentScheduler;
  private selfGeo: GeoHint;
  private active = false;
  private tickTimer: ReturnType<typeof setInterval> | null = null;
  private reportTimer: ReturnType<typeof setInterval> | null = null;
  /** Highest media-sequence the player has requested — the prefetch floor. */
  private maxRequestedSn = -1;
  /** Segment keys currently being prefetched (bounds concurrency). */
  private readonly prefetching = new Set<string>();
  /** When false, this peer serves/advertises nothing (pure leech) — e.g. on cellular. */
  private uploadEnabled = true;

  /** Back-compat alias — same object, viewed through the flat Telemetry shape. */
  get telemetry(): Telemetry {
    return this.metrics;
  }

  constructor(options: EngineOptions) {
    this.config = { ...DEFAULT_CONFIG, ...options };
    this.peerId = randomId();
    this.selfGeo = {
      asn: options.geoSim?.asn ?? null,
      country: options.geoSim?.country ?? null,
      region: options.geoSim?.region ?? null,
    };

    this.store = new SegmentStore(this.config.sharingWindowSize, this.config.maxStoreBytes);
    this.registry = new PeerRegistry(() => this.selfGeo);
    this.signaling = new SignalingClient(this.config, this.peerId);
    this.manager = new PeerConnectionManager(
      this.config,
      this.signaling,
      (seg) => (this.uploadEnabled ? this.store.get(seg) : undefined), // gate serving
      (peerId, ms) => this.metrics.recordRtt(peerId, ms),
    );
    this.scheduler = new SegmentScheduler(
      this.config,
      this.registry,
      this.store,
      this.metrics,
      (add, drop) => this.announceAll(add, drop),
    );

    // The tracker is authoritative for our own geo (derived from our IP).
    this.signaling.on("peers", (msg) => {
      this.selfGeo = msg.self;
    });
    this.manager.on("peer-open", (peer) => {
      this.registry.add(peer);
      this.metrics.activePeers = this.registry.size;
      // Tell the new peer everything we already hold so it can pull from us
      // (unless upload is disabled — then we advertise nothing).
      if (this.uploadEnabled) peer.announceHave(this.store.held());
      // When this peer advertises new segments, consider prefetching them.
      peer.on("have", () => this.schedulePrefetch());
    });
    this.manager.on("peer-closed", (id) => {
      this.registry.remove(id);
      this.metrics.dropPeer(id);
      this.metrics.activePeers = this.registry.size;
    });
  }

  /** Join the swarm and start P2P. Call this on the player's `play` event. */
  activate(): void {
    if (this.active) return;
    this.active = true;
    this.signaling.connect();
    this.signaling.join();
    // Sample one time-series point per second for the dashboard charts.
    this.tickTimer = setInterval(() => this.metrics.tick(), 1000);
    // Report a compact snapshot to the tracker for the swarm-wide dashboard.
    if (this.config.metricsReportIntervalMs > 0) this.reportMetrics(); // immediate: appear without delay
    this.startReportTimer();
  }

  /** (Re)start the metrics report timer from the current interval knob (0 = off). */
  private startReportTimer(): void {
    if (this.reportTimer) {
      clearInterval(this.reportTimer);
      this.reportTimer = null;
    }
    const interval = this.config.metricsReportIntervalMs;
    if (interval > 0) this.reportTimer = setInterval(() => this.reportMetrics(), interval);
  }

  /**
   * Hot-reload tuning knobs on a running engine (from the tracker's /config poll).
   * Only the numeric HotKnobs are applied — structural fields (trackerUrl,
   * streamId, iceServers) are ignored. Sub-components read the shared config by
   * reference, so changes take effect on their next read; the metrics timer is
   * restarted here when its interval changes.
   */
  updateConfig(knobs: Partial<Pick<EngineConfig, HotKnob>>): void {
    const cfg = this.config as Mutable<EngineConfig>;
    let intervalChanged = false;
    for (const k of HOT_KNOBS) {
      const v = knobs[k];
      if (typeof v !== "number" || !Number.isFinite(v) || v === cfg[k]) continue;
      cfg[k] = v;
      if (k === "metricsReportIntervalMs") intervalChanged = true;
    }
    if (intervalChanged && this.active) this.startReportTimer();
  }

  /** Current tuning knobs (reflects any hot-reload) — for telemetry/inspection. */
  get tuning(): Readonly<Pick<EngineConfig, HotKnob>> {
    const c = this.config;
    return {
      maxPeers: c.maxPeers,
      peerRequestConcurrency: c.peerRequestConcurrency,
      prefetchAhead: c.prefetchAhead,
      maxConcurrentPrefetch: c.maxConcurrentPrefetch,
      metricsSampleRate: c.metricsSampleRate,
      metricsReportIntervalMs: c.metricsReportIntervalMs,
      httpOnlyDeadlineMs: c.httpOnlyDeadlineMs,
      hedgeAtDeadlineFraction: c.hedgeAtDeadlineFraction,
      peerRequestTimeoutMs: c.peerRequestTimeoutMs,
      maxPeerRttMs: c.maxPeerRttMs,
    };
  }

  private reportMetrics(): void {
    // Sample down the tracker fan-in at scale (default rate 1 = every peer reports).
    if (this.config.metricsSampleRate < 1 && Math.random() >= this.config.metricsSampleRate) return;
    const s = this.metrics.snapshot();
    this.signaling.sendMetrics({
      offloadRatio: s.offloadRatio,
      bytesP2P: s.bytesP2P,
      bytesHttp: s.bytesHttp,
      segmentsP2P: s.segmentsP2P,
      segmentsHttp: s.segmentsHttp,
      latP2PP50: s.latency.p2p.p50,
      latP2PP95: s.latency.p2p.p95,
      latHttpP50: s.latency.http.p50,
      latHttpP95: s.latency.http.p95,
      deadlineMissRate: s.deadline.missRate,
      hedgeWinRate: s.hedgeWinRate,
      activePeers: s.activePeers,
    });
  }

  /** Leave the swarm and tear down the mesh (e.g. on pause/stop/destroy). */
  deactivate(): void {
    if (!this.active) return;
    this.active = false;
    if (this.tickTimer) {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
    if (this.reportTimer) {
      clearInterval(this.reportTimer);
      this.reportTimer = null;
    }
    this.manager.closeAll();
    this.signaling.leave();
  }

  get isActive(): boolean {
    return this.active;
  }

  /** Number of connected peers currently advertising this segment. */
  providerCount(seg: SegmentId): number {
    return this.manager.peers().filter((p) => p.has(seg)).length;
  }

  /**
   * Resolve a segment, choosing peer vs CDN under the given play deadline.
   * `httpFetch` is the CDN fallback the caller wires to its HTTP stack.
   */
  getSegment(seg: SegmentId, deadlineMs: number, httpFetch: HttpFetch): Promise<SegmentResult> {
    // Track playback position (by media sequence) and top up the prefetch window.
    if (seg.sn >= 0 && seg.sn > this.maxRequestedSn) {
      this.maxRequestedSn = seg.sn;
      this.schedulePrefetch();
    }
    return this.scheduler.getSegment(seg, deadlineMs, httpFetch);
  }

  /**
   * Pull upcoming segments that a peer already advertises, ahead of the player
   * asking for them — the core of LIVE offload. Scans connected peers' advertised
   * segments for ones just past the playhead (within `prefetchAhead`), not yet
   * held/in-flight, and prefetches up to `maxConcurrentPrefetch` at a time.
   */
  private prefetchScheduled = false;

  /**
   * Coalesce prefetch triggers: a busy swarm fires many `have` events per tick,
   * each of which used to run a full O(peers × advertised) scan. Collapse a
   * synchronous burst into one scan via a microtask, and don't even schedule when
   * the prefetch window is already full.
   */
  private schedulePrefetch(): void {
    if (this.prefetchScheduled || !this.active) return;
    if (this.prefetching.size >= this.config.maxConcurrentPrefetch) return;
    this.prefetchScheduled = true;
    queueMicrotask(() => {
      this.prefetchScheduled = false;
      this.runPrefetch();
    });
  }

  private runPrefetch(): void {
    const ahead = this.config.prefetchAhead;
    if (!this.active || ahead <= 0 || this.maxRequestedSn < 0) return;
    // Window full → skip the whole scan (the hottest saving: no work when saturated).
    if (this.prefetching.size >= this.config.maxConcurrentPrefetch) return;

    const candidates = new Map<string, SegmentId>();
    for (const peer of this.manager.peers()) {
      for (const s of peer.advertised()) {
        if (s.sn <= this.maxRequestedSn || s.sn > this.maxRequestedSn + ahead) continue;
        const key = segmentKey(s);
        if (this.prefetching.has(key)) continue;
        candidates.set(key, s); // dedupe across peers by key
      }
    }

    const reserved = Math.max(1, this.config.peerRequestConcurrency - 1);
    const ordered = [...candidates.values()].sort((a, b) => a.sn - b.sn);
    for (const seg of ordered) {
      if (this.prefetching.size >= this.config.maxConcurrentPrefetch) break;
      // Only schedule if a peer can actually serve it now — otherwise skip and
      // retry on the next trigger (avoids a hot re-queue loop when saturated).
      if (!this.registry.pick(seg, reserved)) continue;
      const key = segmentKey(seg);
      this.prefetching.add(key);
      void this.scheduler.prefetch(seg).then((ok) => {
        this.prefetching.delete(key);
        this.schedulePrefetch(); // slot freed (ok or not) — try to fill the window; cheap early-exit if nothing to do
      });
    }
  }

  private announceAll(add: SegmentId[], drop: SegmentId[]): void {
    if (!this.uploadEnabled) return; // don't advertise new segments while upload is off
    for (const peer of this.manager.peers()) peer.announceHave(add, drop);
  }

  /**
   * Enable/disable serving to peers (uploading). On a cellular/metered link,
   * disable to avoid spending the user's mobile data — this peer still downloads
   * from peers/CDN, it just stops advertising and serving segments. Toggling
   * re-advertises (enable) or drops (disable) our held set to connected peers.
   */
  setUploadEnabled(enabled: boolean): void {
    if (enabled === this.uploadEnabled) return;
    this.uploadEnabled = enabled;
    const held = this.store.held();
    if (held.length === 0) return;
    for (const peer of this.manager.peers()) {
      if (enabled) peer.announceHave(held, []); // re-advertise as a source
      else peer.announceHave([], held); // drop: peers stop requesting from us
    }
  }

  get isUploadEnabled(): boolean {
    return this.uploadEnabled;
  }
}

function randomId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `peer-${Math.floor(Math.random() * 1e9).toString(36)}-${Date.now().toString(36)}`;
}
